import { Product } from 'shared/types/product/Product';
import { Variant } from "shared/types/product/Variant";
import { getCategoryLink } from "../product/product-details/helpers/getCategoryURL";
import { ITEM_NAME } from 'helpers/constants/attributes';

interface JsonBreadcrumbProps {
  currentUrl: string;
  product?:Product;
  variant?:Variant;

}

interface JsonBreadcrumbItem {
  name: string;
  item: string;
}


const generateBreadcrumbsJsonList = (url: string): JsonBreadcrumbItem[] => {
  const urlObj = new URL(url);
  const baseUrl = `${urlObj.origin}`;
  const pathSegments = urlObj.pathname.split('/').filter(Boolean); // Remove empty segments

  const breadcrumbs: { name: string; item: string }[] = [];

  const skipNames = ['collection', 'renovation guide'];

  pathSegments.forEach((_, index) => {
    if(index!=0){
      const segmentsSlice = pathSegments.slice(0, index + 1);
      const itemUrl = `${baseUrl}/${segmentsSlice.join('/')}/`;

      // Convert segment to title case label
      const name = decodeURIComponent(segmentsSlice[segmentsSlice.length - 1])
      .replace(/-/g, ' ')
      .replace(/\band\b/gi, '&')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    
      if(!skipNames.includes(name.toLocaleLowerCase())) breadcrumbs.push({ name, item: itemUrl });
    }
  });
  return breadcrumbs;
}

const JsonBreadcrumbSchema: React.FC<JsonBreadcrumbProps> = ({ currentUrl, product, variant }) => {
  
  const jsonBreadcrumbs: JsonBreadcrumbItem[] = [];

  const url = new URL(currentUrl);
  const pathnameParts = url.pathname.split('/').filter(Boolean); // Remove empty parts
  const firstSegment = pathnameParts[0] || '';
  jsonBreadcrumbs.push({ name: 'Home', item: `${url.origin}/${firstSegment}/` });

  if(product){
    const categoryURL = getCategoryLink(product?.categories?.[0]?.categoryId?.toString()); // e.g., '/bathroom/toilet-suites/'
    const categorySegments = categoryURL.split('/').filter(Boolean); // ['bathroom', 'toilet-suites']

    let breadcrumbPath = `${url.origin}/${firstSegment}/`;

    categorySegments.forEach((segment) => {
      breadcrumbPath += `${segment}/`;
      const name = segment
        .replace(/-/g, ' ')
        .replace(/ and /gi, ' & ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
      jsonBreadcrumbs.push({ name, item: breadcrumbPath });
    });

    const productName = typeof variant?.attributes?.[ITEM_NAME] === 'string'
    ? variant?.attributes?.[ITEM_NAME].toLowerCase().includes("turoa")
      ? variant?.attributes?.[ITEM_NAME].replace(/turoa/gi, "Tūroa")
      : variant?.attributes?.[ITEM_NAME]
    : product.name && product.name.toLowerCase().includes("turoa")
    ? product.name.replace(/turoa/gi, "Tūroa")
    : product.name ?? '';
    jsonBreadcrumbs.push({ name: productName, item: currentUrl });    
  }
  else{
    jsonBreadcrumbs.push(...generateBreadcrumbsJsonList(currentUrl));
  }
  
  const structuredBreadcrumbs = jsonBreadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: crumb.item
  }));

  const jsonLdContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: structuredBreadcrumbs
  }, null, 2);

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdContent }} />
  );
};

export default JsonBreadcrumbSchema;
